/**
 * Who answered, one row per person, and how long they had been using the feature.
 *
 * `daysSinceAnchor` is stamped at submit time (R17) and is the only thing that separates, after
 * the fact, an opinion formed in a week from one formed in six. It is what the cohort filter here
 * reads.
 *
 * **The filter applies to this list, not to the cards above.** `GET /:id/results` takes only
 * `?version` — there is no `daysSinceAnchor` parameter on it — so filtering the aggregates would
 * mean recomputing averages in the browser from a paginated sample, which is a different number
 * wearing the same label. The panel says so out loud rather than quietly disagreeing with the
 * cards.
 *
 * Every status is listed, not just the sent ones: abandonment and refusal are findings, and
 * hiding them here would make the screen look like everyone who opened the survey finished it.
 *
 * **Erasing a row reopens the survey for that person.** The response is what makes them ineligible,
 * so `DELETE /:id/responses/:platformUserId` puts them back in the audience — the same shape as
 * `reset_viewer` on a tour. The confirmation says both halves out loud, because "tem certeza?" would
 * let somebody reopen a survey thinking they were tidying a list. Behind `survey:purge`, which only
 * `super_admin` holds; without it the button is **absent**, not disabled.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { UTLoading, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useState } from 'react';
import type { JSX } from 'react';
import { useDeleteSurveyRespondent } from '../../../Hooks/useDeleteSurveyRespondent';
import type { SurveyResponseStatus, SurveyResponseView } from '../../../Types/Surveys.types';
import { formatCount, formatDate, formatDuration, languageName } from '../../../Utils/surveyFormat';
import { ConfirmationDialog } from '@/Shared/components/ConfirmationDialog/ConfirmationDialog';
import { IconButton } from '@/Shared/components/IconButton/IconButton';
import { Pagination } from '@/Shared/components/Pagination/Pagination';
import type { Status } from '@/Shared/components/StatusTag/StatusTag';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
`;

const filtersStyle = css`
  display: flex;
  align-items: flex-end;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 140px;
`;

const labelStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

const selectStyle = css`
  height: 40px;
  padding: 0 var(--space-s);
  border-radius: var(--radius-control);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: var(--font-size-sm);
`;

const spacerStyle = css`
  flex: 1;
`;

const noteStyle = css`
  margin: 0;
  flex: 1 1 320px;
  min-width: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const scrollStyle = css`
  overflow-x: auto;
`;

const tableStyle = css`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
  min-width: 720px;
`;

const headStyle = css`
  text-align: left;
  padding: var(--space-s) var(--space-m);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  border-bottom: 1px solid var(--color-border);
  white-space: nowrap;
`;

const cellStyle = css`
  padding: var(--space-s) var(--space-m);
  border-bottom: 1px solid var(--color-divider);
  white-space: nowrap;
`;

const monoCellStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-xs);
`;

const actionCellStyle = css`
  padding: 4px var(--space-m);
  border-bottom: 1px solid var(--color-divider);
  width: 1%;
  text-align: right;
`;

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
`;

const emptyStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
`;

const STATUS_LABELS: Record<SurveyResponseStatus, string> = {
  SUBMITTED: 'Enviada',
  IN_PROGRESS: 'Abandonada no meio',
  DISMISSED: 'Dispensada',
};

const STATUS_TONES: Record<SurveyResponseStatus, Status> = {
  SUBMITTED: 'success',
  IN_PROGRESS: 'pending',
  DISMISSED: 'neutral',
};

export interface ResultsRespondentsProps {
  surveyId: string;
  /**
   * Whether erasing a respondent is offered at all.
   *
   * Both gates upstream: the Flagsmith flag says this panel offers it, `survey:purge` on the profile
   * says the BFF would allow it. False hides the column entirely — a disabled button would still
   * advertise an action nobody here can take.
   */
  canDelete: boolean;
  responses: SurveyResponseView[];
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  status: string;
  minDays: string;
  maxDays: string;
  onStatusChange: (status: string) => void;
  onMinDaysChange: (value: string) => void;
  onMaxDaysChange: (value: string) => void;
  onPageChange: (page: number) => void;
}

export const ResultsRespondents = ({
  surveyId,
  canDelete,
  responses,
  total,
  page,
  pageSize,
  isLoading,
  status,
  minDays,
  maxDays,
  onStatusChange,
  onMinDaysChange,
  onMaxDaysChange,
  onPageChange,
}: ResultsRespondentsProps): JSX.Element => {
  const min = minDays.trim() === '' ? null : Number(minDays);
  const max = maxDays.trim() === '' ? null : Number(maxDays);

  /** Who the confirmation is about. `null` closes it — the dialog is mounted only while it is set. */
  const [toErase, setToErase] = useState<SurveyResponseView | null>(null);
  const erase = useDeleteSurveyRespondent(surveyId);

  const shown = responses.filter(response => {
    if (min === null && max === null) return true;
    if (response.daysSinceAnchor === null) return false;
    if (min !== null && Number.isFinite(min) && response.daysSinceAnchor < min) return false;
    if (max !== null && Number.isFinite(max) && response.daysSinceAnchor > max) return false;

    return true;
  });

  return (
    <div className={wrapStyle}>
      <div className={filtersStyle}>
        <div className={fieldStyle} style={{ width: 200 }}>
          <span className={labelStyle}>Status</span>
          <select
            id='survey-respondents-status'
            className={selectStyle}
            value={status}
            onChange={event => {
              onStatusChange(event.target.value);
            }}
          >
            <option value=''>Todos os status</option>
            {(Object.keys(STATUS_LABELS) as SurveyResponseStatus[]).map(entry => (
              <option key={entry} value={entry}>
                {STATUS_LABELS[entry]}
              </option>
            ))}
          </select>
        </div>

        <div className={fieldStyle}>
          <span className={labelStyle}>Dias desde a âncora · de</span>
          <UTTextField
            id='survey-respondents-min-days'
            type='number'
            placeholder='0'
            value={minDays}
            onChange={event => {
              onMinDaysChange(event.target.value);
            }}
          />
        </div>

        <div className={fieldStyle}>
          <span className={labelStyle}>até</span>
          <UTTextField
            id='survey-respondents-max-days'
            type='number'
            placeholder='sem limite'
            value={maxDays}
            onChange={event => {
              onMaxDaysChange(event.target.value);
            }}
          />
        </div>

        <div className={spacerStyle} />
        <p className={noteStyle}>
          O recorte por dias vale para esta lista. Os cartões acima continuam cobrindo a versão
          inteira: o endpoint de resultados aceita só a versão, e recalcular as médias aqui, sobre
          uma página, daria outro número com o mesmo rótulo.
        </p>
      </div>

      {isLoading && responses.length === 0 ? (
        <UTLoading type='dots' />
      ) : shown.length === 0 ? (
        <p id='survey-respondents-empty' className={emptyStyle}>
          Nenhuma resposta nesta faixa de dias.
        </p>
      ) : (
        <div className={scrollStyle}>
          <table id='survey-respondents-table' className={tableStyle}>
            <thead>
              <tr>
                <th className={headStyle}>Platform user</th>
                <th className={headStyle}>Status</th>
                <th className={headStyle}>Dias desde a âncora</th>
                <th className={headStyle}>Passos vistos</th>
                <th className={headStyle}>Duração</th>
                <th className={headStyle}>Idioma</th>
                <th className={headStyle}>Enviada em</th>
                {canDelete ? <th className={headStyle}>Ação</th> : null}
              </tr>
            </thead>
            <tbody>
              {shown.map(response => (
                <tr key={response.id} id={`survey-respondent-${response.platformUserId}`}>
                  <td className={`${cellStyle} ${monoCellStyle}`}>{response.platformUserId}</td>
                  <td className={cellStyle}>
                    <StatusTag status={STATUS_TONES[response.status]} size='sm' dot>
                      {STATUS_LABELS[response.status]}
                    </StatusTag>
                  </td>
                  <td className={cellStyle}>
                    {response.daysSinceAnchor === null
                      ? '—'
                      : formatCount(response.daysSinceAnchor)}
                  </td>
                  <td className={cellStyle}>{response.stepsSeen ?? '—'}</td>
                  <td className={cellStyle}>{formatDuration(response.durationMs)}</td>
                  <td className={cellStyle}>{languageName(response.language)}</td>
                  <td className={cellStyle}>{formatDate(response.submittedAt)}</td>
                  {canDelete ? (
                    <td className={actionCellStyle}>
                      <IconButton
                        id={`survey-respondent-delete-${response.platformUserId}`}
                        icon='Misc.Trash'
                        variant='danger'
                        size='sm'
                        disabled={erase.isPending}
                        aria-label={`Apagar a resposta do usuário ${response.platformUserId}`}
                        onClick={() => {
                          setToErase(response);
                        }}
                      />
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > pageSize ? (
        <div className={footerStyle}>
          <Pagination
            id='survey-respondents-pagination'
            page={page}
            pageSize={pageSize}
            total={total}
            onChange={onPageChange}
          />
        </div>
      ) : null}

      {/* UTModal keeps its content in the a11y tree when closed, so mount it only when needed. */}
      {toErase ? (
        <ConfirmationDialog
          open
          title='Apagar a resposta desta pessoa'
          message={`A resposta do usuário ${toErase.platformUserId} será apagada desta pesquisa, em todas as versões dela, e não há como desfazer. A pessoa volta a ficar elegível: a pesquisa aparece para ela de novo no próximo acesso, e ela pode responder outra vez.`}
          confirmText='Apagar e reabrir'
          cancelText='Cancelar'
          variant='danger'
          isLoading={erase.isPending}
          onClose={() => {
            setToErase(null);
          }}
          onConfirm={() => {
            /*
              Nothing leaves the screen before the `200`. The row goes when the refetch says it is
              gone; a failure leaves it exactly where it was, which is what the toast then explains.
            */
            erase.mutate(toErase.platformUserId, {
              onSettled: () => {
                setToErase(null);
              },
            });
          }}
        />
      ) : null}
    </div>
  );
};
